import React, { useEffect, useState } from 'react';
import { Box, Typography, Container, Paper } from '@mui/material';
import usePageTitle from '../../hooks/usePageTitle';
import PublicPageLayout from '../../components/layout/PublicPageLayout';
import CanonicalLink from '../../components/common/CanonicalLink';
import MarkdownRenderer from '../../components/common/MarkdownRenderer';
import { parseMarkdownWithFrontmatter, formatLegalDate } from '../../utils/markdownLoader';

// Import the markdown content as a string
import termsOfServiceMarkdown from '../../content/legal/terms-of-service.md';

// Export the current version for use in other components
export let CURRENT_TOS_VERSION = '2025-06-05'; // Default value, will be updated

const TermsOfServicePage: React.FC = () => {
  const [content, setContent] = useState<string>('');
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  // Set the page title
  usePageTitle('Terms of Service - WaveScoop', false);

  // Load and parse markdown content
  useEffect(() => {
    try {
      // The markdown is already loaded as a string by raw-loader
      const { content: markdownContent, metadata } = parseMarkdownWithFrontmatter(termsOfServiceMarkdown);
      
      setContent(markdownContent);
      setLastUpdated(formatLegalDate(metadata.lastUpdated));
      
      // Update the exported version
      if (metadata.version) {
        CURRENT_TOS_VERSION = metadata.version;
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error parsing terms of service:', error);
      setIsLoading(false);
    }
  }, []);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (isLoading) {
    return (
      <PublicPageLayout>
        <Container maxWidth="lg">
          <Box sx={{ my: 4 }}>
            <Typography>Loading...</Typography>
          </Box>
        </Container>
      </PublicPageLayout>
    );
  }

  return (
    <PublicPageLayout>
      <CanonicalLink />
      <Box sx={{ flexGrow: 1, py: { xs: 2, md: 4 } }}>
        <Container maxWidth="lg">
          <Box sx={{ my: 4 }}>
            <Typography variant="h2" component="h1" gutterBottom sx={{ color: 'white' }}>
              WaveScoop Terms of Service
            </Typography>
            {lastUpdated && (
              <Typography variant="subtitle1" paragraph sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                Last updated: {lastUpdated}
              </Typography>
            )}
          </Box>

          <Paper 
            elevation={3} 
            sx={{ 
              p: { xs: 2, sm: 3, md: 4 },
              mb: 4,
              bgcolor: 'rgba(10, 30, 60, 0.85)',
              backdropFilter: 'blur(10px)',
              borderRadius: 2,
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            <MarkdownRenderer content={content} />
          </Paper>
        </Container>
      </Box>
    </PublicPageLayout>
  );
};

export default TermsOfServicePage;